import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle2, Loader2, Play, X, Zap } from "lucide-react";
import CoinTossStage from "../bond-coin-pool/CoinTossStage";
import BondCoinFairPanel from "../bond-coin/BondCoinFairPanel";
import {
  coinFlipBuy,
  coinFlipCancel,
  coinFlipPlay,
  fetchCoinFlipConstants,
  fetchCoinFlipLanding,
  clearCoinFlipLastSettled,
  clearCoinFlipPendingRound,
  selectCoinFlipActionLoading,
  selectCoinFlipConstants,
  selectCoinFlipLastSettled,
  selectCoinFlipPendingRound,
} from "../../redux/coinFlipSlice";
import { patchAppUser } from "../../redux/appAuthSlice";
import {
  computeCoinFlipQuote,
  formatCoinFlipCoin,
  resolveBondCoinFlipSettledRound,
  sideLabel,
} from "../../utils/coinFlipHelpers";
import { usePendingPlayCountdown } from "../../hooks/usePendingPlayCountdown";
import useBondDiceResultDwell from "../../hooks/useBondDiceResultDwell";
import useRevealAnimation from "../../hooks/useRevealAnimation";
import BondPlayReservedTicket from "../bond-play/BondPlayReservedTicket";

const BondCoinFlipStage = ({
  isAuthenticated,
  userCoin,
  onSignIn,
  onNotify,
}) => {
  const dispatch = useDispatch();
  const constants = useSelector(selectCoinFlipConstants);
  const pendingRound = useSelector(selectCoinFlipPendingRound);
  const lastSettled = useSelector(selectCoinFlipLastSettled);
  const actionLoading = useSelector(selectCoinFlipActionLoading);

  const [playerSide, setPlayerSide] = useState("head");
  const [stakeInput, setStakeInput] = useState("500");
  const [revealTicket, setRevealTicket] = useState(null);
  const {
    isRevealing: tossSpinning,
    revealedRound,
    resetReveal,
    finishReveal,
    runReveal,
  } = useRevealAnimation();

  const stake = useMemo(() => Math.floor(Number(stakeInput) || 0), [stakeInput]);
  const minStake = constants?.minStake || 100;
  const maxStake = constants?.maxStake || 500000;
  const playTtlMs = constants?.pendingPlayTtlMs || 60 * 1000;
  const hasPending = pendingRound?.status === "Pending";
  const visualPendingRound = hasPending ? pendingRound : tossSpinning ? revealTicket : null;
  const showPending = !!visualPendingRound;

  const handleTicketExpired = useCallback(() => {
    resetReveal();
    setRevealTicket(null);
    const stake = Number(pendingRound?.stake || 0);
    dispatch(clearCoinFlipPendingRound());
    dispatch(fetchCoinFlipLanding());
    if (stake > 0) {
      dispatch(patchAppUser({ COIN: Math.max(0, userCoin + stake) }));
    }
    onNotify?.({
      variant: "refund",
      game: "flip",
      title: "Flip expired",
      subtitle: "Your stake was returned to your balance.",
      amount: stake,
      autoCloseMs: 3500,
    });
  }, [dispatch, onNotify, pendingRound?.stake, resetReveal, userCoin]);

  const { secondsLeft, expired: ticketExpired } = usePendingPlayCountdown({
    active: showPending,
    playExpiresAt: visualPendingRound?.playExpiresAt,
    createdAt: visualPendingRound?.createdAt,
    ttlMs: playTtlMs,
    onExpired: handleTicketExpired,
  });

  useEffect(() => {
    if (!constants) dispatch(fetchCoinFlipConstants());
  }, [constants, dispatch]);

  const ticketRound = revealTicket || pendingRound;

  const resolvedRound = useMemo(
    () =>
      resolveBondCoinFlipSettledRound({
        revealedRound,
        lastSettled,
        ticketRound,
      }),
    [revealedRound, lastSettled, ticketRound]
  );

  useEffect(() => {
    if (tossSpinning && resolvedRound) finishReveal(resolvedRound);
  }, [tossSpinning, resolvedRound, finishReveal]);

  const handleResultCleared = useCallback(() => {
    resetReveal();
    dispatch(clearCoinFlipLastSettled());
  }, [dispatch, resetReveal]);

  const displaySettledRound = useBondDiceResultDwell(resolvedRound, { onCleared: handleResultCleared });

  const flipResultRound = displaySettledRound ?? resolvedRound;
  const isTossSpinning = Boolean(ticketRound && !resolvedRound && (tossSpinning || actionLoading));
  const showPendingUi = showPending && !resolvedRound;

  const liveQuote = useMemo(
    () => computeCoinFlipQuote(stake, constants),
    [constants, stake]
  );

  const canBuy =
    isAuthenticated &&
    !showPendingUi &&
    !isTossSpinning &&
    stake >= minStake &&
    stake <= maxStake &&
    stake <= userCoin;

  const handleBuy = async () => {
    try {
      await dispatch(coinFlipBuy({ playerSide, stake })).unwrap();
      resetReveal();
      setRevealTicket(null);
      onNotify?.({
        variant: "ticket",
        game: "flip",
        title: "Flip ticket secured",
        subtitle: "Your coin toss is locked — flip before the timer runs out.",
        pick: sideLabel(playerSide),
        stake,
        maxWin: liveQuote?.maxWin,
        autoCloseMs: 4000,
      });
    } catch (error) {
      onNotify?.(typeof error === "string" ? error : "Could not reserve flip");
    }
  };

  const handleFlip = async () => {
    if (!pendingRound?._id) return;
    setRevealTicket(pendingRound);
    try {
      const round = await runReveal(() => dispatch(coinFlipPlay({ roundId: pendingRound._id })).unwrap());
      setRevealTicket(null);
      const won = round?.status === "Won";
      const net = Number(round?.playerNetPayout || 0);
      onNotify?.(
        won
          ? {
              variant: "win",
              game: "flip",
              title: "You won the flip!",
              subtitle: `${sideLabel(round.outcome)} matched your pick.`,
              pick: sideLabel(round.playerSide || playerSide),
              outcome: round.outcome,
              stake: Number(round?.stake || pendingRound?.stake || 0),
              netPayout: net,
            }
          : {
              variant: "loss",
              game: "flip",
              title: "Wrong side",
              subtitle: `${sideLabel(round.outcome)} won this flip.`,
              pick: sideLabel(round.playerSide || playerSide),
              outcome: round.outcome,
              stake: Number(round?.stake || pendingRound?.stake || 0),
            }
      );
    } catch (error) {
      onNotify?.(typeof error === "string" ? error : "Flip failed");
    }
  };

  const handleCancel = async () => {
    if (!pendingRound?._id) return;
    try {
      await dispatch(
        coinFlipCancel({ roundId: pendingRound._id, stake: Number(pendingRound.stake || 0) })
      ).unwrap();
      resetReveal();
      setRevealTicket(null);
      onNotify?.({
        variant: "refund",
        game: "flip",
        title: "Flip canceled",
        subtitle: "Your stake is back in your wallet.",
        amount: Number(pendingRound.stake || 0),
        autoCloseMs: 3500,
      });
    } catch (error) {
      onNotify?.(typeof error === "string" ? error : "Cancel failed");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bcoin-empty">
        <p>Sign in to play Instant Flip.</p>
        <button type="button" className="bcoin-btn bcoin-btn-primary" onClick={onSignIn}>
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="bcoin-stage bcoin-flip-stage">
      <header className="bcoin-head">
        <div>
          <p className="bcoin-eyebrow">Instant Flip</p>
          <h1 className="bcoin-title bcoin-flip-title" aria-label="Fixed 2x coin toss">
            <span className="bcoin-flip-title-multiplier">2x</span>
          </h1>
        </div>
        <span className={`bcoin-status ${showPendingUi ? "bcoin-status-pending" : isTossSpinning ? "bcoin-status-pending" : "bcoin-status-muted"}`}>
          {showPendingUi ? "Reserved" : isTossSpinning ? "Flipping" : "Ready"}
        </span>
      </header>

      <CoinTossStage
        outcome={flipResultRound?.outcome ?? null}
        spinning={isTossSpinning}
        locked={showPendingUi && !isTossSpinning}
      />

      {flipResultRound?.outcome ? (
        <p className="bcoin-round-outcome">
          Outcome: <strong>{sideLabel(flipResultRound.outcome)}</strong>
          {flipResultRound.playerSide ? (
            <>
              {" "}
              · Your pick: {sideLabel(flipResultRound.playerSide)}
              {flipResultRound.status === "Won" ? (
                <> · Won {formatCoinFlipCoin(flipResultRound.playerNetPayout || 0)} COIN</>
              ) : null}
            </>
          ) : null}
        </p>
      ) : (
        <p className="bcoin-round-outcome bcoin-round-outcome-placeholder" aria-hidden>
          &nbsp;
        </p>
      )}

      {(flipResultRound?.seedHash || visualPendingRound?.seedHash) ? (
        <BondCoinFairPanel round={flipResultRound || visualPendingRound} game="flip" compact />
      ) : null}

      {showPendingUi ? (
        <BondPlayReservedTicket
          variant="flip"
          actionVerb="Flip"
          pickLabel={sideLabel(visualPendingRound?.playerSide)}
          stakeLabel={`${formatCoinFlipCoin(visualPendingRound?.stake)} COIN`}
          maxWinLabel={`${formatCoinFlipCoin(visualPendingRound?.maxPotentialPayout)} COIN`}
          playExpiresAt={visualPendingRound?.playExpiresAt}
          createdAt={visualPendingRound?.createdAt}
          ttlMs={playTtlMs}
          secondsLeft={secondsLeft}
          expired={ticketExpired}
        />
      ) : (
        <>
          <div className="bcoin-side-toggle">
            <button
              type="button"
              className={`bcoin-side-btn head ${playerSide === "head" ? "active" : ""}`}
              onClick={() => setPlayerSide("head")}
            >
              Head
              {playerSide === "head" ? <CheckCircle2 className="bcoin-side-check" size={16} strokeWidth={2.6} /> : null}
            </button>
            <button
              type="button"
              className={`bcoin-side-btn tail ${playerSide === "tail" ? "active" : ""}`}
              onClick={() => setPlayerSide("tail")}
            >
              Tail
              {playerSide === "tail" ? <CheckCircle2 className="bcoin-side-check" size={16} strokeWidth={2.6} /> : null}
            </button>
          </div>

          <div className="bcoin-join-row">
            <label className="bcoin-stake-field">
              Stake (COIN)
              <input
                type="number"
                min={minStake}
                max={maxStake}
                value={stakeInput}
                onChange={(event) => setStakeInput(event.target.value)}
              />
            </label>
          </div>

          {liveQuote ? (
            <p className="bcoin-share-preview">
              Max win {formatCoinFlipCoin(liveQuote.maxWin)} COIN · Pool reserve {formatCoinFlipCoin(liveQuote.reserve)} COIN
            </p>
          ) : null}
        </>
      )}

      <div className="bcoin-flip-actions">
        {showPendingUi ? (
          <>
            <button
              type="button"
              className="bcoin-btn"
              disabled={actionLoading || isTossSpinning}
              onClick={handleCancel}
            >
              <X size={14} />
              Cancel
            </button>
            <button
              type="button"
              className="bcoin-btn bcoin-btn-primary"
              disabled={actionLoading || isTossSpinning || ticketExpired}
              onClick={handleFlip}
            >
              {isTossSpinning ? <Loader2 size={14} className="bcoin-spin" /> : <Play size={14} />}
              {isTossSpinning ? "Flipping…" : "Flip now"}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="bcoin-btn bcoin-btn-primary bcoin-flip-buy"
            disabled={actionLoading || !canBuy}
            onClick={handleBuy}
          >
            {actionLoading ? <Loader2 size={14} className="bcoin-spin" /> : <Zap size={14} />}
            <span className="bcoin-reserve-copy">
              <span>{actionLoading ? "Reserving…" : "Reserve ticket"}</span>
              {liveQuote?.maxWin && !actionLoading ? (
                <span className="bcoin-reserve-potential">
                  Win up to {formatCoinFlipCoin(liveQuote.maxWin)} COIN
                </span>
              ) : null}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default BondCoinFlipStage;
